import { ChangeDetectionStrategy, Component, computed, effect, inject, input } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { toSignal } from '@angular/core/rxjs-interop';
import { echarts } from '../../../../../../charts/echarts';
import {
  TtoAudienceDataDto,
  TtoContentPerformanceDto,
  TtoResultsDashboardEngagementsDto,
} from '../../../../../../common/general-types/tiktok-one-reporting.dto';
import { FormatService } from '../../../../../../common/format/format.service';
import { ThemeService } from '../../../../../../services/theme/theme.service';
import { NgxEchartsDirective } from 'ngx-echarts';
import type { EChartsOption } from 'echarts';

export type EngagementMetric = {
  key: string;
  label: string;
  /** Full SVG path `d` attributes — one entry per <path> element */
  svgPaths: string[];
  /** Full Tailwind class string applied to the SVG element */
  iconClass: string;
  /** Formatted total value (organic + paid for counts; organic rate for percentages) */
  total: string;
  /** Formatted organic value, or null if no breakdown is available */
  organic: string | null;
  /** Formatted paid value, or null when paid data is absent */
  paid: string | null;
};

const SOURCE_LABELS: Record<string, string> = {
  for_you: 'For You',
  others: 'Others',
  personal_profile: 'Personal Profile',
  search: 'Search',
  following: 'Following',
  hashtag: 'Hashtag',
  sound: 'Sound',
  unknown: 'Unknown',
};

@Component({
  selector: 'ttor-overview',
  imports: [NgxEchartsDirective],
  templateUrl: './overview.html',
  styleUrl: './overview.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TtoOverviewComponent {
  ttoEngagements = input.required<TtoResultsDashboardEngagementsDto>();
  contentPerformance = input.required<TtoContentPerformanceDto>();
  audienceData = input.required<TtoAudienceDataDto>();

  private fmt = inject(FormatService);
  private doc = inject(DOCUMENT);
  private http = inject(HttpClient);
  private themeService = inject(ThemeService);

  readonly worldGeoJson = toSignal(
    this.http.get<unknown>('assets/geo/world.json'),
  );

  constructor() {
    effect(() => {
      const geo = this.worldGeoJson();
      if (geo) {
        echarts.registerMap('world', geo as Parameters<typeof echarts.registerMap>[1]);
      }
    });
  }

  readonly metrics = computed<EngagementMetric[]>(() => {
    const e = this.ttoEngagements();

    const n = (val: number | null): string =>
      val != null ? this.fmt.convertIntToString(val) : '—';

    const totalCount = (organic: number, paid: number | null): string =>
      this.fmt.convertIntToString(organic + (paid ?? 0));

    const rate = (val: number | null): string =>
      val != null ? `${val.toFixed(1)}%` : '—';

    return [
      {
        key: 'views',
        label: 'Views',
        svgPaths: [
          'M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0',
          'M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0',
        ],
        iconClass: 'size-4 shrink-0 text-sky-500',
        total: totalCount(e.views.organic, e.views.paid),
        organic: n(e.views.organic),
        paid: e.views.paid != null ? n(e.views.paid) : null,
      },
      {
        key: 'engagement_rate',
        label: 'Eng. Rate',
        svgPaths: [
          'M22 7 13.5 15.5l-5-5L2 17',
          'M16 7h6v6',
        ],
        iconClass: 'size-4 shrink-0 text-primary',
        total: rate(e.engagement_rate.organic),
        organic: rate(e.engagement_rate.organic),
        paid: e.engagement_rate.paid != null ? rate(e.engagement_rate.paid) : null,
      },
      {
        key: 'engagement_count',
        label: 'Engagements',
        svgPaths: [
          'M22 12h-2.48a2 2 0 0 0-1.93 1.46l-2.35 8.36a.25.25 0 0 1-.48 0L9.24 2.18a.25.25 0 0 0-.48 0l-2.35 8.36A2 2 0 0 1 4.49 12H2',
        ],
        iconClass: 'size-4 shrink-0 text-accent',
        total: n(e.engagement_count),
        organic: null,
        paid: null,
      },
      {
        key: 'likes',
        label: 'Likes',
        svgPaths: [
          'M2 9.5a5.5 5.5 0 0 1 9.591-3.676.56.56 0 0 0 .818 0A5.49 5.49 0 0 1 22 9.5c0 2.29-1.5 4-3 5.5l-5.492 5.313a2 2 0 0 1-3 .019L5 15c-1.5-1.5-3-3.2-3-5.5',
        ],
        iconClass: 'size-4 shrink-0 text-rose-500',
        total: totalCount(e.likes.organic, e.likes.paid),
        organic: n(e.likes.organic),
        paid: e.likes.paid != null ? n(e.likes.paid) : null,
      },
      {
        key: 'comments',
        label: 'Comments',
        svgPaths: [
          'M2.992 16.342a2 2 0 0 1 .094 1.167l-1.065 3.29a1 1 0 0 0 1.236 1.168l3.413-.998a2 2 0 0 1 1.099.092 10 10 0 1 0-4.777-4.719',
        ],
        iconClass: 'size-4 shrink-0 text-teal-500',
        total: totalCount(e.comments.organic, e.comments.paid),
        organic: n(e.comments.organic),
        paid: e.comments.paid != null ? n(e.comments.paid) : null,
      },
      {
        key: 'shares',
        label: 'Shares',
        svgPaths: [
          'M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8',
          'M16 6 12 2 8 6',
          'M12 2v13',
        ],
        iconClass: 'size-4 shrink-0 text-accent',
        total: totalCount(e.shares.organic, e.shares.paid),
        organic: n(e.shares.organic),
        paid: e.shares.paid != null ? n(e.shares.paid) : null,
      },
      {
        key: 'six_seconds_views',
        label: '6s Views',
        svgPaths: [
          'M12 6v6l4 2',
          'M3.05 11a9 9 0 1 1 .5 4m-.5 5v-5h5',
        ],
        iconClass: 'size-4 shrink-0 text-amber-500',
        total: totalCount(e.six_seconds_views.organic, e.six_seconds_views.paid),
        organic: n(e.six_seconds_views.organic),
        paid: e.six_seconds_views.paid != null ? n(e.six_seconds_views.paid) : null,
      },
    ];
  });

  readonly contentMetrics = computed<EngagementMetric[]>(() => {
    const cp = this.contentPerformance();

    const views = (val: number): string => this.fmt.convertIntToString(val);
    const secs = (val: number): string => `${val.toFixed(1)}s`;
    const pct = (val: number): string => `${(val * 100).toFixed(2)}%`;
    // Rate metrics: show "—" when paid is 0 (no paid data — rate is meaningless)
    const pctOrNull = (val: number): string | null => val === 0 ? null : `${(val * 100).toFixed(2)}%`;
    const secsOrNull = (val: number): string | null => val === 0 ? null : `${val.toFixed(1)}s`;

    return [
      {
        key: 'video_views',
        label: 'Video Views',
        svgPaths: [
          'M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0',
          'M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0',
        ],
        iconClass: 'size-4 shrink-0 text-sky-500',
        total: views(cp.video_views.total),
        organic: views(cp.video_views.organic),
        paid: views(cp.video_views.paid),
      },
      {
        key: 'average_view_time',
        label: 'Avg View Time',
        svgPaths: ['M12 6v6l4 2', 'M3.05 11a9 9 0 1 1 .5 4m-.5 5v-5h5'],
        iconClass: 'size-4 shrink-0 text-accent',
        total: secs(cp.average_view_time.total),
        organic: secs(cp.average_view_time.organic),
        paid: secsOrNull(cp.average_view_time.paid),
      },
      {
        key: 'video_completion_rate',
        label: 'Completion Rate',
        svgPaths: [
          'M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10',
          'M9 12l2 2 4-4',
        ],
        iconClass: 'size-4 shrink-0 text-primary',
        total: pct(cp.video_completion_rate.total),
        organic: pct(cp.video_completion_rate.organic),
        paid: pctOrNull(cp.video_completion_rate.paid),
      },
      {
        key: 'two_second_video_views',
        label: '2s Video Views',
        svgPaths: [
          'M12 6v6l4 2',
          'M3.05 11a9 9 0 1 1 .5 4m-.5 5v-5h5',
        ],
        iconClass: 'size-4 shrink-0 text-teal-500',
        total: pct(cp.two_second_video_views.total),
        organic: pct(cp.two_second_video_views.organic),
        paid: pctOrNull(cp.two_second_video_views.paid),
      },
      {
        key: 'six_second_video_views',
        label: '6s Video Views',
        svgPaths: [
          'M12 6v6l4 2',
          'M3.05 11a9 9 0 1 1 .5 4m-.5 5v-5h5',
        ],
        iconClass: 'size-4 shrink-0 text-amber-500',
        total: pct(cp.six_second_video_views.total),
        organic: pct(cp.six_second_video_views.organic),
        paid: pctOrNull(cp.six_second_video_views.paid),
      },
    ];
  });

  readonly sourceChartOption = computed<EChartsOption>(() => {
    const sources = this.contentPerformance().video_views_by_source
      .filter(s => s.videoViews > 0);
    return this.buildDoughnutOption(
      sources.map(s => ({ name: SOURCE_LABELS[s.source] ?? s.source, value: s.videoViews })),
    );
  });

  // ── Audience chart computeds ──────────────────────────────────────────────

  private static readonly GENDER_LABELS: Record<string, string> = {
    FEMALE: 'Female', MALE: 'Male', Other: 'Other',
  };

  private static readonly DEVICE_LABELS: Record<string, string> = {
    ios: 'iOS', android: 'Android',
  };

  private static readonly AGE_ORDER = ['13-17', '18-24', '25-34', '35-44', '45-54', '55+'];

  private static readonly REGION_NAMES = new Intl.DisplayNames(['en'], { type: 'region' });

  readonly genderChartOption = computed<EChartsOption>(() =>
    this.buildDoughnutOption(
      this.audienceData().genders_distribution.map(g => ({
        name: TtoOverviewComponent.GENDER_LABELS[g.gender] ?? g.gender,
        value: +(g.percentage * 100).toFixed(2),
      })),
    )
  );

  readonly deviceChartOption = computed<EChartsOption>(() =>
    this.buildDoughnutOption(
      this.audienceData().device_distribution.map(d => ({
        name: TtoOverviewComponent.DEVICE_LABELS[d.device] ?? d.device,
        value: +(d.percentage * 100).toFixed(2),
      })),
    )
  );

  readonly languageChartOption = computed<EChartsOption>(() => {
    const rn = TtoOverviewComponent.REGION_NAMES;
    return this.buildDoughnutOption(
      this.audienceData().language_distribution.map(l => ({
        name: (() => { try { return rn.of(l.language.toUpperCase()) ?? l.language; } catch { return l.language; } })(),
        value: +(l.percentage * 100).toFixed(2),
      })),
    );
  });

  readonly ageChartOption = computed<EChartsOption>(() => {
    const order = TtoOverviewComponent.AGE_ORDER;
    const sorted = [...this.audienceData().age_distribution].sort(
      (a, b) => order.indexOf(a.age_group) - order.indexOf(b.age_group),
    );
    const labelColor = this.getForegroundColor();
    return {
      animation: false,
      backgroundColor: 'transparent',
      grid: { left: 40, right: 16, top: 16, bottom: 32 },
      tooltip: { trigger: 'axis', formatter: (p: unknown) => {
        const params = p as Array<{ name: string; value: number }>;
        return `${params[0].name}: ${params[0].value.toFixed(1)}%`;
      }},
      xAxis: {
        type: 'category',
        data: sorted.map(a => a.age_group),
        axisLabel: { color: labelColor, fontSize: 11 },
        axisLine: { lineStyle: { color: 'rgba(128,128,128,0.2)' } },
        axisTick: { show: false },
      },
      yAxis: {
        type: 'value',
        axisLabel: { formatter: '{value}%', color: labelColor, fontSize: 10 },
        splitLine: { lineStyle: { type: 'dashed', color: 'rgba(128,128,128,0.15)' } },
      },
      series: [{
        type: 'bar',
        data: sorted.map(a => +(a.percentage * 100).toFixed(2)),
        barMaxWidth: 48,
        itemStyle: {
          color: '#6508fb',
          borderRadius: [4, 4, 0, 0],
        },
      }],
    };
  });

  readonly topCountries = computed(() => {
    const rn = TtoOverviewComponent.REGION_NAMES;
    return this.audienceData().countries_distribution.slice(0, 5).map(c => ({
      name: (() => { try { return rn.of(c.country) ?? c.country; } catch { return c.country; } })(),
      percentage: c.percentage,
    }));
  });

  readonly hasSourceData = computed(() =>
    this.contentPerformance().video_views_by_source.some(s => s.videoViews > 0)
  );
  readonly hasGenderData = computed(() =>
    this.audienceData().genders_distribution.length > 0
  );
  readonly hasDeviceData = computed(() =>
    this.audienceData().device_distribution.length > 0
  );
  readonly hasAgeData = computed(() =>
    this.audienceData().age_distribution.length > 0
  );
  readonly hasLanguageData = computed(() =>
    this.audienceData().language_distribution.length > 0
  );
  readonly hasCountriesData = computed(() =>
    this.audienceData().countries_distribution.length > 0
  );

  readonly worldMapOption = computed<EChartsOption>(() => {
    const rn = TtoOverviewComponent.REGION_NAMES;
    const countries = this.audienceData().countries_distribution;
    const maxPct = countries[0]?.percentage ?? 1;
    const dark = this.themeService.isDarkMode();

    const data = countries.map(c => ({
      name: (() => { try { return rn.of(c.country) ?? c.country; } catch { return c.country; } })(),
      value: +(c.percentage * 100).toFixed(2),
    }));

    return {
      animation: false,
      backgroundColor: 'transparent',
      tooltip: {
        trigger: 'item',
        formatter: (params: unknown) => {
          const p = params as { name: string; value: number };
          return p.value != null ? `${p.name}: ${p.value.toFixed(2)}%` : p.name;
        },
      },
      visualMap: {
        min: 0,
        max: +(maxPct * 100).toFixed(2),
        show: false,
        // Light: pale violet → primary. Dark: deep purple → brighter violet for contrast on dark surfaces.
        inRange: { color: dark ? ['#3b0764', '#a78bfa'] : ['#ede9fe', '#6508fb'] },
      },
      series: [{
        type: 'map',
        map: 'world',
        roam: false,
        emphasis: {
          label: { show: false },
          itemStyle: { areaColor: dark ? '#c4b5fd' : '#bf8cff' },
        },
        select: { disabled: true },
        itemStyle: {
          areaColor: dark ? '#374151' : '#e5e7eb',
          borderColor: dark ? '#111827' : '#fff',
          borderWidth: 0.5,
        },
        data,
      }],
    };
  });

  // ── Private helpers ───────────────────────────────────────────────────────

  private getPrimaryLabelColor(): string {
    const raw = getComputedStyle(this.doc.documentElement).getPropertyValue('--primary').trim();
    return raw || '#6508fb';
  }

  private getForegroundColor(): string {
    const raw = getComputedStyle(this.doc.documentElement).getPropertyValue('--foreground').trim();
    return raw || '#111827';
  }

  private buildDoughnutOption(data: Array<{ name: string; value: number }>): EChartsOption {
    const labelColor = this.getPrimaryLabelColor();
    return {
      animation: false,
      backgroundColor: 'transparent',
      color: ['#6508fb', '#38bdf8', '#db24bf', '#f59e0b', '#f87171', '#95159e', '#2dd4bf', '#fb923c'],
      tooltip: { trigger: 'item', formatter: '{b}: {d}%' },
      series: [{
        type: 'pie',
        radius: ['45%', '68%'],
        center: ['50%', '50%'],
        itemStyle: {
          borderRadius: 8,
          borderColor: 'rgba(255,255,255,0.08)',
          borderWidth: 3,
        },
        label: {
          show: true,
          formatter: '{b}\n{d}%',
          fontSize: 11,
          lineHeight: 16,
          color: labelColor,
          textBorderWidth: 0,
        },
        labelLine: { show: true, length: 10, length2: 12 },
        emphasis: {
          itemStyle: { shadowBlur: 10, shadowOffsetX: 0, shadowColor: 'rgba(0,0,0,0.2)' },
        },
        data,
      }],
    };
  }
}
